import React from 'react';
import {FlatList, Pressable, View} from 'react-native';
import {SvgCssUri} from 'react-native-svg';
import {Image, makeStyles} from '@rn-vui/themed';
import {observer} from 'mobx-react';

import {useWebScreenDimensions, WebScreenDimensionsType} from '../../hooks/useWebScreenDimensions';
import {AirtableAttachmentDetail, AirtableBrand} from '../../models/airtable';
import {useStores} from '../../stores/use-stores';
import TitleLine from '../rewards/TitleLine';
import Text from '../themed/Text';

function ShopByBrand({
  isLoading,
  brands,
  onSeeAllPress,
  onBrandPress,
}: {
  isLoading?: boolean;
  brands: string[];
  onSeeAllPress: () => void;
  onBrandPress: (item: AirtableBrand) => void;
}) {
  const {brandsStore} = useStores();
  const filterBrand =
    brands && brands.length > 0 ? (item: AirtableBrand) => brands.includes(item.id) : () => true;
  const visibleBrands = brandsStore.brands.filter(filterBrand).slice(0, 9);

  if (!isLoading && visibleBrands.length === 0) {
    return null;
  }

  return (
    <View>
      <TitleLine title="SHOP BY BRAND" onSeeAllPress={onSeeAllPress} />
      {!isLoading && <LogoGrid brands={visibleBrands} onBrandPress={onBrandPress} />}
    </View>
  );
}

const BrandImage = (detail: AirtableAttachmentDetail) => {
  const styles = useStyles(useWebScreenDimensions());

  const supportedImageExtensions = ['image/png', 'image/jpg', 'image/jpeg', 'image/tiff'];
  if (supportedImageExtensions.includes(detail.type)) {
    return (
      <View style={[styles.logoImageHolder]}>
        <Image source={{uri: detail.url}} containerStyle={styles.logoImage} resizeMode="contain" />
      </View>
    );
  } else if (detail.type.endsWith('svg+xml')) {
    return (
      <View style={styles.logoImageSVG}>
        <SvgCssUri width="95%" height="95%" uri={detail?.url || ''} />
      </View>
    );
  } else {
    return (
      <View style={styles.logoImage}>
        <Text>unk</Text>
      </View>
    );
  }
};

const BrandLogoCard = ({
  onBrandPress,
  ...item
}: AirtableBrand & {
  onBrandPress: (item: AirtableBrand) => void;
}) => {
  const styles = useStyles(useWebScreenDimensions());
  const props = item.logo?.[0];
  return (
    <Pressable
      testID={`brand-card-${item.id}`}
      onPress={() => {
        onBrandPress(item);
      }}
      style={[styles.brandTileContainerStyle]}>
      <BrandImage {...props} />
    </Pressable>
  );
};

const LogoGrid = ({
  onBrandPress,
  brands,
}: {
  onBrandPress: (item: AirtableBrand) => void;
  brands: AirtableBrand[];
}) => {
  const {isWebDesktopScreen} = useWebScreenDimensions();
  const styles = useStyles({isWebDesktopScreen});

  const numColumns = isWebDesktopScreen ? 9 : 3;

  return (
    <FlatList
      scrollEnabled={false}
      style={styles.gridContainerStyle}
      data={brands}
      keyExtractor={item => String(item.id)}
      numColumns={numColumns}
      renderItem={({item}) => {
        return <BrandLogoCard onBrandPress={onBrandPress} {...item} key={item.id} />;
      }}
    />
  );
};

const useStyles = makeStyles(
  (theme, {isWebDesktopScreen}: Pick<WebScreenDimensionsType, 'isWebDesktopScreen'>) => {
    return {
      gridContainerStyle: {
        marginVertical: -8,
        marginHorizontal: -6,
      },
      brandTileContainerStyle: {
        flex: isWebDesktopScreen ? 1 / 9 : 1 / 3,
        marginVertical: 8,
        marginHorizontal: 6,
      },
      logoImageHolder: {
        borderWidth: 1,
        borderColor: theme.colors.greyOutline,
        elevation: 2,
        borderRadius: 4,
        backgroundColor: 'white',
        aspectRatio: 1,
        padding: 8,
      },
      logoImage: {
        height: '100%',
        aspectRatio: 1,
        backgroundColor: 'white',
      },
      logoImageSVG: {
        backgroundColor: 'white',
        height: '90%',
        aspectRatio: 1,
        justifyContent: 'center',
        alignItems: 'center',

        shadowColor: '#000',
        shadowOffset: {
          width: 0,
          height: 5,
        },
        shadowOpacity: 0.29,
        shadowRadius: 4.65,
        elevation: 2,
      },
    };
  },
);

export default observer(ShopByBrand);
